import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useOrg } from "../../lib/org";
import { Card, ErrorState, Money, PageHeader, Spinner } from "../../components/ui";
import { today } from "../../lib/format";
import { DateInput, ReportControls } from "./controls";
import type { TrialBalance as TB } from "@shared/types";

export default function TrialBalance() {
  const { currentOrg } = useOrg();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const [asOf, setAsOf] = useState(today());

  const { data, isLoading, error } = useQuery({
    queryKey: ["tb", currentOrg?.id, asOf],
    queryFn: () => api.get<TB>(`/reports/trial-balance?asOf=${asOf}`),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader title="Trial Balance" subtitle={`As of ${asOf}`} />
      <ReportControls>
        <DateInput label="As of" value={asOf} onChange={setAsOf} />
      </ReportControls>

      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <Card className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="th w-24">Code</th>
                <th className="th">Account</th>
                <th className="th text-right">Debit</th>
                <th className="th text-right">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.map((r) => (
                <tr key={r.accountId}>
                  <td className="td font-mono text-gray-400">{r.code ?? "—"}</td>
                  <td className="td">{r.name}</td>
                  <td className="td text-right">
                    {r.debit ? <Money cents={r.debit} currency={currency} /> : ""}
                  </td>
                  <td className="td text-right">
                    {r.credit ? <Money cents={r.credit} currency={currency} /> : ""}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 font-bold">
                <td className="td" colSpan={2}>
                  Totals
                </td>
                <td className="td text-right">
                  <Money cents={data.totalDebit} currency={currency} />
                </td>
                <td className="td text-right">
                  <Money cents={data.totalCredit} currency={currency} />
                </td>
              </tr>
            </tfoot>
          </table>
        </Card>
      )}
    </div>
  );
}
